import { useState } from 'react'

import { TeamMemberRole } from '@api'
import { useUnit } from 'effector-react'
import last from 'lodash/last'
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query'

import { $account } from '@/models/account.ts'
import { $creatorId } from '@/models/creators.ts'
import { $hasReassignPermission } from '@/models/permissions'
import { notificationsService } from '@/modules/chatting/service/notifications'
import type { NotificationTypeRequest } from '@/modules/chatting/types'
import { useTeamForReassignment } from '@/modules/team/hooks/use-fetch-team-table.tsx'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import NotificationIcon from '../../../icons/notification.svg'
import { IconButton } from '../../components/icon-button'
import { NotificationsDialog } from './dialog'

export const notificationTypes: {
  name: string
  id: NotificationTypeRequest
}[] = [
  { name: 'All', id: 'all' },
  { name: 'Subscriptions', id: 'subscribed' },
  { name: 'Purchases', id: 'purchases' },
  { name: 'Tips', id: 'tip' },
]

export const Notifications = () => {
  const [isBarOpen, setIsBarOpen] = useState(false)
  const [type, setType] = useState<NotificationTypeRequest>(
    notificationTypes[0].id,
  )
  const hasReassignPermission = useUnit($hasReassignPermission)
  const creatorId = useUnit($creatorId)
  const account = useUnit($account)
  const queryClient = useQueryClient()
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useInfiniteQuery(
    ['notifications', type, creatorId],
    ({ pageParam = 0 }) => {
      return notificationsService.getNotifications({
        anchorId: pageParam,
        creatorId: creatorId!,
        type,
      })
    },
    {
      getNextPageParam: ({ hasMore, list }) =>
        hasMore ? last(list)?.id : undefined,
      enabled: !!creatorId && isBarOpen,
      onSuccess: () => {
        const notificationTypesToMarkRead =
          type === 'all' ? notificationTypes.map(({ id }) => id) : [type]
        const newUnreadNotificationsCountState =
          notificationTypesToMarkRead.reduce<
            Partial<Record<NotificationTypeRequest, number>>
          >((acc, type) => {
            acc[type] = 0

            return acc
          }, {})

        queryClient.setQueryData(
          ['unreadNotificationsCount', creatorId],
          (prev: unknown) => {
            if (!prev) return newUnreadNotificationsCountState

            return {
              ...prev,
              ...newUnreadNotificationsCountState,
            }
          },
        )
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while getting notifications')
      },
    },
  )
  const {
    data: unreadNotifications,
    refetch: refetchUnreadNotificationsCount,
  } = useQuery(
    ['unreadNotificationsCount', creatorId],
    () => notificationsService.getUnreadNotificationsCount(creatorId!),
    {
      enabled: !!creatorId,
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while getting unread notifications count',
          refetchUnreadNotificationsCount,
        )
      },
    },
  )

  const notifications =
    data?.pages.flatMap(({ list }) => list).filter(isTruthyAndNotEmpty) ?? []

  const isAdminOrLeader = [TeamMemberRole.ADMIN, TeamMemberRole.LEADER].some(
    (role) => role === account?.memberRole,
  )

  const { data: members, isFetching: isMembersFetching } =
    useTeamForReassignment(creatorId!, {
      refetchInterval: 120000,
      enabled: isAdminOrLeader || hasReassignPermission,
    })

  const { all = 0 } = unreadNotifications ?? {}

  const onClose = () => {
    setIsBarOpen(false)
    queryClient.removeQueries(['notifications'])
  }

  return (
    <>
      <div className="relative h-7 w-7">
        <IconButton
          icon={NotificationIcon}
          iconClassName="h-7 w-7"
          onClick={() => setIsBarOpen(true)}
          tooltip="Notifications"
        />
        {all > 0 && (
          <div className="bg-danger-default absolute -right-0.5 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-xs text-white pointer-events-none">
            {all}
          </div>
        )}
      </div>

      <NotificationsDialog
        isOpen={isBarOpen}
        onClose={onClose}
        type={type}
        onClickItem={(type) => {
          setType((previousType) => {
            // Removing previous data
            queryClient.removeQueries(['notifications', previousType])

            return type
          })
        }}
        notifications={notifications}
        onLoadMore={fetchNextPage}
        canLoadMore={hasNextPage ?? false}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={(isFetching && !isFetchingNextPage) || isLoading}
        onCloseNotificationsBar={onClose}
        onReload={() => {
          queryClient.resetQueries(['notifications'])
        }}
        unreadNotificationsCountsMap={unreadNotifications}
        members={members}
        isMembersFetching={isMembersFetching}
      />
    </>
  )
}
